import getDeliveryAvailableDateAPI from './api/getAvailableDate'
import { IGetDeliveryAvailableDateRequest } from './interfaces/IGetAvailableDateRequest'
import {
  CalendarExceptions,
  DeliveryException,
  FetchCalendarExceptionsParams,
  GetDeliveryDatesPayload
} from '@/store/modules/deliveryDates/interfaces/calendarException'
import { fetchExceptions } from '@/store/modules/deliveryDates/api/getCalendarExceptions'
import { fetchExceptionsFromMagento } from '@/store/modules/deliveryDates/api/getCalendarExceptionsFromMagento'
import {
  filterCalendarExceptions,
  filterFutureExceptions,
  isRelevantException,
  processDeliveryExceptions,
  extractPosmInventoryDays,
  isStandardDeliveryDate,
  getHolidayDays,
  getUTCDate
} from '@/utils/deliveryDate'
import { IShipToBySapId } from '@/store/modules/catalog/interfaces/shipto/IShipToResponse'
import { IGetDeliveryAvailableDateResponse } from '@/store/modules/deliveryDates/interfaces/IGetAvailableDateResponse'
import { DeliveryDatesMutationTypes } from '@/store/modules/deliveryDates/interfaces/mutation-types'
import { DeliveryDatesActionTypes } from '@/store/modules/deliveryDates/interfaces/action-types'
import { IDeliveryDatesState } from '@/store/modules/deliveryDates/interfaces/state'
import { IDeliveryDatesMutations } from '@/store/modules/deliveryDates/interfaces/IDeliveryDatesMutation'
import { IDeliveryDatesActions } from '@/store/modules/deliveryDates/interfaces/IDeliveryDatesAction'
import { TransportationTypes } from '@/utils/transportTypes'
import { apolloClient } from '@/store/graphql.config'
import { AcknowledgePopup } from './queries/AcknowledgePopup'

async function resolveHolidayDays (context: any): Promise<string[]> {
  let holidaysConfig = context.rootState?.main?.holidaysConfig
  if (!Array.isArray(holidaysConfig) || holidaysConfig.length === 0) {
    await context.dispatch('main/FETCH_HOLIDAYS_CONFIG', null, { root: true })
    holidaysConfig = context.rootState?.main?.holidaysConfig
  }
  return getHolidayDays(holidaysConfig)
}

function generateNextWorkingDay (holidayDays: string[] = []): string {
  const cursor = new Date()
  cursor.setDate(cursor.getDate() + 1)
  while (cursor.getDay() === 0 || cursor.getDay() === 6 || holidayDays.includes(getUTCDate(cursor))) {
    cursor.setDate(cursor.getDate() + 1)
  }
  cursor.setUTCHours(0, 0, 0, 0)
  return cursor.toISOString().replace(/\.\d{3}Z$/, 'Z')
}

function resetDeliveryDataToNextWorkingDay (commit: any, shipTos: IShipToBySapId[] = [], shipmentId = '', holidayDays: string[] = []): void {
  const nextWorkingDay = generateNextWorkingDay(holidayDays)
  const isStandard = isStandardDeliveryDate(nextWorkingDay, shipmentId, shipTos)
  localStorage.setItem('standardDeliveryDate', isStandard ? '1' : '0')
  commit(DeliveryDatesMutationTypes.SET_AVAILABLE_DAYS, [nextWorkingDay])
  commit(DeliveryDatesMutationTypes.SET_FILTERED_AVAILABLE_DAYS, [nextWorkingDay])
  commit(DeliveryDatesMutationTypes.SET_UNAVAILABLE_DATES, [])
  commit(DeliveryDatesMutationTypes.SET_SCHEDULE_DATES, [])
  commit(DeliveryDatesMutationTypes.SET_AVAILABLE_EXCEPTIONS, [])
  commit(DeliveryDatesMutationTypes.SET_NOTIFICATION_EXCEPTIONS, [])
  commit(DeliveryDatesMutationTypes.SET_POSM_INVENTORY_DATES, [])
}

const state = (): IDeliveryDatesState => ({
  availableDays: [],
  filteredDeliveryDates: [],
  unavailableDates: [],
  scheduleDates: [],
  availableExceptions: [],
  notificationExceptions: [],
  isPopUpSeen: true,
  posmInventoryDates: [],
  isExceptionFallback: false,
  isDateApiFallback: false,
  isDeliveryDateFallback: false,
  datesRequestPayload: {
    shipTo: '',
    shipmentId: '',
    store: ''
  },
  exeptionRequestPayload: {
    guid: '',
    shipTo: '',
    store: ''
  }
})

const getters = {}

const mutations: IDeliveryDatesMutations = {
  [DeliveryDatesMutationTypes.SET_AVAILABLE_DAYS] (state, payload: string[]) {
    state.availableDays = payload
  },
  [DeliveryDatesMutationTypes.SET_FILTERED_AVAILABLE_DAYS] (state, payload: string[]) {
    state.filteredDeliveryDates = payload
  },
  [DeliveryDatesMutationTypes.SET_UNAVAILABLE_DATES] (state, payload: string[]) {
    state.unavailableDates = payload
  },
  [DeliveryDatesMutationTypes.SET_SCHEDULE_DATES] (state, payload: string[]) {
    state.scheduleDates = payload
  },
  [DeliveryDatesMutationTypes.SET_AVAILABLE_EXCEPTIONS] (state, payload: DeliveryException[]) {
    state.availableExceptions = payload
  },
  [DeliveryDatesMutationTypes.SET_NOTIFICATION_EXCEPTIONS] (state, payload: CalendarExceptions[]) {
    state.notificationExceptions = payload
  },
  [DeliveryDatesMutationTypes.SET_SEEN_POPUP] (state, payload: any) {
    state.isPopUpSeen = payload
  },
  [DeliveryDatesMutationTypes.SET_DATES_REQUESTS_PAYLOAD] (state, payload: GetDeliveryDatesPayload) {
    state.datesRequestPayload = payload
  },
  [DeliveryDatesMutationTypes.SET_EXEPTION_REQUESTS_PAYLOAD] (state, payload: FetchCalendarExceptionsParams) {
    state.exeptionRequestPayload = payload
  },
  [DeliveryDatesMutationTypes.SET_POSM_INVENTORY_DATES] (state, payload: CalendarExceptions[]) {
    state.posmInventoryDates = payload
  },
  [DeliveryDatesMutationTypes.SET_EXCEPTION_FALLBACK] (state, payload: boolean) {
    state.isExceptionFallback = payload
    state.isDeliveryDateFallback = state.isExceptionFallback || state.isDateApiFallback
    if (state.isDeliveryDateFallback) {
      localStorage.setItem('exceptiondate', '0')
    }
  },
  [DeliveryDatesMutationTypes.SET_DATE_API_FALLBACK] (state, payload: boolean) {
    state.isDateApiFallback = payload
    state.isDeliveryDateFallback = state.isExceptionFallback || state.isDateApiFallback
    if (state.isDeliveryDateFallback) {
      localStorage.setItem('exceptiondate', '0')
    }
  }
}

const actions: IDeliveryDatesActions = {
  [DeliveryDatesActionTypes.GET_DELIVERY_AVAILABLE_DATE]: async (context, dto: IGetDeliveryAvailableDateRequest) => {
    const { commit, state } = context as any
    try {
      const sameRequest = state.datesRequestPayload.shipTo === dto.shipTo && state.datesRequestPayload.shipmentId === dto.shipmentId && state.datesRequestPayload.store === dto.store
      if (!sameRequest) {
        if (dto.component !== 'datePicker') {
          commit(DeliveryDatesMutationTypes.SET_DATES_REQUESTS_PAYLOAD, dto)
        } else {
          delete dto.component
        }
        const res: IGetDeliveryAvailableDateResponse = await getDeliveryAvailableDateAPI(dto)

        const deliveryExceptions = res?.deliveryExceptions || []
        const allDatesFromApi = res?.allDeliveryDate || []
        const filteredDatesFromApi = res?.filteredDeliveryDate || []

        const {
          allDates,
          filteredDates,
          unavailableDates,
          scheduleDates
        } = processDeliveryExceptions(
          deliveryExceptions,
      dto.shipmentId as TransportationTypes,
      allDatesFromApi,
      filteredDatesFromApi
        )

        const availableExceptions = filterFutureExceptions(deliveryExceptions)
          .filter((exception) =>
            isRelevantException(exception, dto.shipmentId as TransportationTypes))

        const posmInventoryDates = extractPosmInventoryDays(res.deliveryExceptions)
        commit(DeliveryDatesMutationTypes.SET_POSM_INVENTORY_DATES, posmInventoryDates)
        commit(DeliveryDatesMutationTypes.SET_AVAILABLE_DAYS, allDates)
        commit(DeliveryDatesMutationTypes.SET_FILTERED_AVAILABLE_DAYS, filteredDates)
        commit(DeliveryDatesMutationTypes.SET_UNAVAILABLE_DATES, unavailableDates)
        commit(DeliveryDatesMutationTypes.SET_SCHEDULE_DATES, scheduleDates)
        commit(DeliveryDatesMutationTypes.SET_AVAILABLE_EXCEPTIONS, availableExceptions)
        commit(DeliveryDatesMutationTypes.SET_DATE_API_FALLBACK, false)
      }
    } catch (e) {
      console.warn('GET_DELIVERY_AVAILABLE_DATE failed, entering fallback mode', e)
      const shipTos = (context as any).rootState?.catalog?.shipTos ?? []
      const holidayDays = await resolveHolidayDays(context)
      resetDeliveryDataToNextWorkingDay(commit, shipTos, dto.shipmentId ?? '', holidayDays)
      commit(DeliveryDatesMutationTypes.SET_DATE_API_FALLBACK, true)
    }
  },

  async [DeliveryDatesActionTypes.FETCH_NOTIFICATION_EXCEPTION] (context, params: FetchCalendarExceptionsParams) {
    const { commit, state } = context as any
    try {
      const sameRequest = state.exeptionRequestPayload.guid === params.guid && state.exeptionRequestPayload.shipTo === params.shipTo && state.exeptionRequestPayload.store === params.store
      if (!sameRequest) {
        commit(DeliveryDatesMutationTypes.SET_EXEPTION_REQUESTS_PAYLOAD, params)

        let data: CalendarExceptions[] = []
        try {
          const magentoData = await fetchExceptionsFromMagento([params.shipTo])
          if (magentoData.length > 0) {
            data = magentoData
          } else {
            data = await fetchExceptions(params.guid, params.shipTo, params.store)
          }
        } catch {
          data = await fetchExceptions(params.guid, params.shipTo, params.store)
        }

        const filteredData = filterCalendarExceptions(data)
        commit(DeliveryDatesMutationTypes.SET_SEEN_POPUP, data[0].popupSeen)
        commit(DeliveryDatesMutationTypes.SET_NOTIFICATION_EXCEPTIONS, filteredData)
        commit(DeliveryDatesMutationTypes.SET_EXCEPTION_FALLBACK, false)
      }
    } catch (e) {
      console.warn('FETCH_NOTIFICATION_EXCEPTION failed, entering fallback mode', e)
      const shipTos = (context as any).rootState?.catalog?.shipTos ?? []
      const shipmentId = state.datesRequestPayload.shipmentId ?? ''
      const holidayDays = await resolveHolidayDays(context)
      resetDeliveryDataToNextWorkingDay(commit, shipTos, shipmentId, holidayDays)
      commit(DeliveryDatesMutationTypes.SET_EXCEPTION_FALLBACK, true)
    }
  },
  async [DeliveryDatesActionTypes.SET_EXEPTIONS_SEEN] ({ commit }, dto: any) {
    await apolloClient.mutate<any, any>({
      mutation: AcknowledgePopup,
      variables: {
        shipToIds: dto.shiptoids
      }
    })
  },
  async [DeliveryDatesActionTypes.SET_FETCHED_NOTIFICATION_EXCEPTION] ({ commit }, data: CalendarExceptions[]) {
    commit(DeliveryDatesMutationTypes.SET_NOTIFICATION_EXCEPTIONS, data)
  }
}

const DeliveryDatesModule = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}

export default DeliveryDatesModule
